import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  FaInfoCircle,
  FaNewspaper,
  FaTools,
  FaUserGraduate,
  FaBuilding,
  FaBook,
  FaQuestion,
  FaCaretDown,
  FaGlobe,
  FaBlog,
  FaComments,
  FaGraduationCap,
  FaSignInAlt,
  FaUser,
  FaSignOutAlt,
  FaEnvelope
} from "react-icons/fa";
import { BiSolidReport } from "react-icons/bi";
import {
  MdPolicy,
  MdEvent,
  MdWarning,
  MdLock,
  MdFamilyRestroom,
  MdSecurity,
} from "react-icons/md";
import { IoSchool } from "react-icons/io5";
import { RiTeamFill } from "react-icons/ri";
import EthiopiaFlag from "./ui/EthiopiaFlag";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Bars3Icon, XMarkIcon } from "@heroicons/react/20/solid";
import logo from "../assets/logo.png";
import { useDispatch, useSelector } from "react-redux";
import { setLanguage } from "../redux/languageSlice";
import { useCommunityAuth } from "../contexts/CommunityAuthContext";
import NotificationBell from "../components/community/shared/NotificationBell";

const PageHeader = () => {
  const { t, i18n } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [expandedItem, setExpandedItem] = useState(null);
  const [avatarLoadError, setAvatarLoadError] = useState(false);
  const menuRef = useRef(null);
  const toggleButtonRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  const dispatch = useDispatch();
  const selectedLanguage = useSelector(
    (state) => state.language.selectedLanguage,
  );
  const { user, logout, isAdmin } = useCommunityAuth();

  const avatarUrl =
    user?.avatar_url ||
    user?.avatarUrl ||
    user?.avatar ||
    user?.profile_picture ||
    user?.profilePicture ||
    user?.photo ||
    user?.image ||
    user?.picture ||
    null;

  const languages = [
    { label: "Eng", code: "en", icon: <FaGlobe /> },
    { label: "አማ", code: "am", icon: <EthiopiaFlag className="w-3 h-2" /> },
    // { label: "oro", code: "om", icon: <Flag code="ET" className="w-1 h-1" /> },
  ];

  const handleLanguageChange = (code) => {
    dispatch(setLanguage(code));
  };

  useEffect(() => {
    i18n.changeLanguage(selectedLanguage);
  }, [i18n, selectedLanguage]);

  useEffect(() => {
    setAvatarLoadError(false);
  }, [avatarUrl, user?.id, user?.username]);

  useEffect(() => {
    // console.log('Selected Language:', selectedLanguage);
  }, [selectedLanguage]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        toggleButtonRef.current &&
        toggleButtonRef.current.contains(event.target)
      ) {
        return;
      }

      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isMenuOpen]);

  useEffect(() => {
    if (!isMenuOpen) {
      setExpandedItem(null);
    }
  }, [isMenuOpen]);

  const toggleSubMenu = (item) => {
    setExpandedItem(expandedItem === item ? null : item);
  };

  const handleSignIn = () => {
    navigate("/community/login");
  };

  const handleProfileClick = () => {
    navigate(isAdmin ? "/admin/dashboard" : "/community/profile");
  };

  const handleSignOut = () => {
    logout();
    navigate("/");
  };

  const isActive = (path, subItems) => {
    if (subItems)
      return subItems.some((subItem) => location.pathname === subItem.path);
    return location.pathname === path;
  };

  const menuItems = {
    [t("header.about_us")]: {
      items: [
        {
          label: t("header.about_ethio_cert"),
          icon: <FaInfoCircle />,
          path: "/about-ethio-cert",
        },
        {
          label: t("header.our_services"),
          icon: <FaBuilding />,
          path: "/our-service",
        },
        { label: t("header.rfc_2350"), icon: <FaBook />, path: "/rfc-2350" },
        { label: t("header.faq"), icon: <FaQuestion />, path: "/faq" },
        {
          label: t("header.contact_us"),
          icon: <FaEnvelope />,
          path: "/contact-us",
        },
      ],
    },
    [t("header.news_and_events")]: {
      items: [
        { label: t("header.news"), icon: <FaNewspaper />, path: "/news" },
        { label: t("header.events"), icon: <MdEvent />, path: "/events" },
        { label: t("header.alerts"), icon: <MdWarning />, path: "/alerts" },
        {
          label: t("header.advisories"),
          icon: <MdLock />,
          path: "/advisories",
        },
        { label: t("header.cves"), icon: <MdSecurity />, path: "/cves" },
      ],
    },
    [t("header.resources")]: {
      items: [
        { label: t("header.tools"), icon: <FaTools />, path: "/tools" },
        {
          label: t("header.reports"),
          icon: <BiSolidReport />,
          path: "/reports",
        },
        {
          label: t("header.regulations"),
          icon: <MdPolicy />,
          path: "/regulations",
        },
      ],
    },
    [t("header.awareness")]: {
      items: [
        {
          label: t("header.child_online_protection"),
          icon: <MdFamilyRestroom />,
          path: "/child-online-protection",
        },
        {
          label: t("header.individual"),
          icon: <FaUserGraduate />,
          path: "/individual-training",
        },
        {
          label: t("header.publicprivate"),
          icon: <RiTeamFill />,
          path: "/publicprivate-training",
        },
        {
          label: t("header.professional"),
          icon: <IoSchool />,
          path: "/professional-training",
        },
        {
          label: t("header.family"),
          icon: <MdFamilyRestroom />,
          path: "/family-training",
        },
        {
          label: t("header.youth"),
          icon: <FaUserGraduate />,
          path: "/youth-training",
        },
      ],
    },
[t("header.community")]: {
  items: [
    { label: t("header.blog"), icon: <FaBlog />, path: "/blog" },
    { label: t("header.forum"), icon: <FaComments />, path: "/forum" },
    { label: t("header.learning"), icon: <FaGraduationCap />, path: "/learning" },
  ],
},

  };

  const isHomePage = location.pathname === "/";
  const useTransparentHeader = isHomePage && !isScrolled;

  return (
    <header
      className={`fixed top-0 z-50 w-full font-nunito-sans transition-all ${
        useTransparentHeader
          ? "bg-transparent text-white border-b border-transparent"
          : "bg-white/95 text-secondary backdrop-blur border-b border-gray-100"
      } ${isScrolled ? "shadow-md" : ""}`}
    >
      <div className="w-full px-4 sm:px-6 lg:px-8">
        <div className="relative flex h-16 items-center">
          <div className="flex items-center shrink-0 space-x-4">
            <Link to="/" className="block">
              <span className="sr-only">Home</span>
              <img src={logo} alt="Logo" className="h-8 w-auto" />
            </Link>
            <div className="relative hidden xl:block group rounded-lg z-50 mr-8">
              <button
                className={`flex items-center px-2 py-2 text-body-small font-light`}
              >
                {selectedLanguage === "en" ? (
                  <FaGlobe className="mr-0.5 w-3 h-3" />
                ) : (
                  <EthiopiaFlag className="w-3 h-2 mr-0.5" />
                )}
                <span>
                  {
                    languages.find((lang) => lang.code === selectedLanguage)
                      ?.label
                  }
                </span>
                <FaCaretDown className="ml-0.5 w-3 h-3 transform group-hover:rotate-180 transition-transform" />
              </button>

              <div className="absolute right-0 mt-0.5 w-12 h-4 shadow group-hover:visible opacity-0 group-hover:opacity-100 transition-opacity">
                {languages
                  .filter((lang) => lang.code !== selectedLanguage)
                  .map((lang, i) => (
                    <button
                      key={i}
                      onClick={() => handleLanguageChange(lang.code)}
                      className={`flex items-center w-full px-2 py-1 text-body-small ${!isHomePage || isScrolled ? "bg-white hover:bg-secondary hover:text-white" : "bg-transparent hover:bg-white hover:text-secondary"}`}
                    >
                      {lang.icon}
                      <span className="ml-0.5">{lang.label}</span>
                    </button>
                  ))}
              </div>
            </div>
          </div>

          <div className="hidden xl:flex absolute left-1/2 -translate-x-1/2">
            <nav aria-label="Global">
              <ul className="flex items-center gap-6 text-sm whitespace-nowrap">
                {[...Object.keys(menuItems)].map((item) => {
                  const path = null;
                  const subItems = menuItems[item]?.items;

                  return (
                    <li key={item} className="relative group">
                      <button
                        type="button"
                        className={`flex items-center gap-1 rounded-md px-3 py-2 font-medium transition ${
                          isActive(path, subItems)
                            ? "text-primary"
                            : useTransparentHeader
                              ? "text-white/90 hover:text-white"
                              : "text-secondary/90 hover:text-primary"
                        }`}
                      >
                        <span>{item}</span>
                        {menuItems[item] && (
                          <FaCaretDown className="h-3 w-3 transition-transform group-hover:rotate-180" />
                        )}
                      </button>

                      {menuItems[item] && (
                        <div className="absolute left-0 z-10 mt-0.5 w-64 divide-y divide-gray-100 rounded-md border border-gray-100 bg-white shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all">
                          <div className="p-2">
                            {menuItems[item].items.map((subItem) => (
                              <Link
                                key={subItem.path}
                                to={subItem.path}
                                className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition ${
                                  location.pathname === subItem.path
                                    ? "bg-gray-50 text-gray-900"
                                    : "text-gray-600 hover:bg-gray-50 hover:text-gray-800"
                                }`}
                              >
                                <span className="text-base">
                                  {subItem.icon}
                                </span>
                                <span>{subItem.label}</span>
                              </Link>
                            ))}
                          </div>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </nav>
          </div>

          <div className="ml-auto flex items-center justify-end gap-3 shrink-0">
            <Link
              to="/incident-report"
              className={`relative hidden xl:inline-flex items-center justify-center rounded-md px-4 py-2 text-sm font-medium ${
                useTransparentHeader
                  ? "text-white"
                  : "text-secondary"
              }`}
            >
              <span
                className="absolute inset-0 w-full h-full rounded-md"
                style={{
                  background: "linear-gradient(to right, #21D4FD, #B721FF)",
                  padding: "2px",
                  mask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
                  maskComposite: "exclude",
                  WebkitMaskComposite: "destination-out",
                }}
              ></span>
              <span className="relative">{t("header.report_incident")}</span>
            </Link>

            {!user ? (
              <button
                onClick={handleSignIn}
                className={`relative hidden xl:inline-flex items-center justify-center rounded-md px-4 py-2 text-sm font-medium shadow-sm transition focus:outline-none focus:ring-2 focus:ring-primary/50 ${
                  useTransparentHeader
                    ? "bg-white/10 text-white hover:bg-white/15"
                    : "bg-primary text-white hover:bg-shake"
                }`}
              >
                {useTransparentHeader && (
                  <span
                    className="absolute inset-0 w-full h-full rounded-md"
                    style={{
                      background: "linear-gradient(to right, #2Ba8CB, #008BFF)",
                      padding: "2px",
                      mask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
                      maskComposite: "exclude",
                      WebkitMaskComposite: "destination-out",
                    }}
                  ></span>
                )}
                <span className="inline-flex items-center gap-2">
                  <FaSignInAlt className="text-xs" />
                  Sign In
                </span>
              </button>
            ) : (
              <div className="flex items-center gap-4">
                <div className="hidden xl:block">
                  <NotificationBell />
                </div>
                <div className="hidden xl:relative xl:block group">
                  <button
                    type="button"
                    className={`overflow-hidden rounded-full border shadow-inner ${
                      useTransparentHeader
                        ? "border-white/30"
                        : "border-gray-300 dark:border-gray-700"
                    }`}
                    onClick={handleProfileClick}
                  >
                    <span className="sr-only">Toggle dashboard menu</span>
                    <div
                      className={`size-10 flex items-center justify-center ${
                        useTransparentHeader
                          ? "bg-white/10 text-white"
                          : "bg-gray-100 text-gray-700"
                      }`}
                    >
                      {avatarUrl && !avatarLoadError ? (
                        <img
                          src={avatarUrl}
                          alt={user?.username || "Profile"}
                          className="h-full w-full object-cover"
                          onError={() => setAvatarLoadError(true)}
                        />
                      ) : (
                        <FaUser className="text-sm" />
                      )}
                    </div>
                  </button>

                  <div
                    className="absolute end-0 z-10 mt-0.5 w-56 divide-y divide-gray-100 rounded-md border border-gray-100 bg-white shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all"
                    role="menu"
                  >
                    <div className="p-2">
                      <button
                        onClick={handleProfileClick}
                        className="flex w-full items-center gap-2 rounded-lg px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 hover:text-gray-800"
                        role="menuitem"
                      >
                        <FaUser className="text-xs" />
                        <span>My profile</span>
                      </button>
                    </div>
                    <div className="p-2">
                      <button
                        type="button"
                        onClick={handleSignOut}
                        className="flex w-full items-center gap-2 rounded-lg px-4 py-2 text-sm text-red-700 hover:bg-red-50"
                        role="menuitem"
                      >
                        <FaSignOutAlt className="text-sm" />
                        <span>Logout</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="flex items-center gap-2 xl:hidden">
              {user && (
                <div className="mr-1">
                  <NotificationBell />
                </div>
              )}
              <button
                onClick={() => setIsMenuOpen((v) => !v)}
                ref={toggleButtonRef}
                className={`inline-flex items-center justify-center rounded-md p-2.5 transition ${
                  useTransparentHeader
                    ? "text-white hover:bg-white/10"
                    : "text-gray-700 hover:bg-gray-100"
                }`}
              >
                <span className="sr-only">Open main menu</span>
                {isMenuOpen ? (
                  <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                ) : (
                  <Bars3Icon className="h-6 w-6" aria-hidden="true" />
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {isMenuOpen && (
        <div
          className="xl:hidden border-t border-gray-100 bg-white shadow-lg"
          ref={menuRef}
        >
          <div className="w-full px-4 sm:px-6 lg:px-8 py-4 max-h-[calc(100vh-4rem)] overflow-y-auto">
            <div className="flex items-center justify-between gap-3 mb-3">
              <Link
                to="/incident-report"
                className="relative inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-gray-700"
                onClick={() => setIsMenuOpen(false)}
              >
                <span
                  className="absolute inset-0 w-full h-full rounded-md"
                  style={{
                    background: "linear-gradient(to right, #21D4FD, #B721FF)",
                    padding: "2px",
                    mask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
                    maskComposite: "exclude",
                    WebkitMaskComposite: "destination-out",
                  }}
                ></span>
                <span className="relative">{t("header.report_incident")}</span>
              </Link>

              <div className="relative group">
                <button
                  type="button"
                  aria-haspopup="menu"
                  aria-label="Change language"
                  className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white/70 px-3 py-2 text-sm font-medium text-gray-700 shadow-sm transition hover:bg-white hover:text-secondary focus:outline-none focus:ring-2 focus:ring-primary/50"
                >
                  {selectedLanguage === "en" ? (
                    <FaGlobe className="h-4 w-4 text-primary" />
                  ) : (
                    <EthiopiaFlag className="h-3 w-4 rounded-sm" />
                  )}
                  <span className="leading-none">
                    {
                      languages.find((lang) => lang.code === selectedLanguage)
                        ?.label
                    }
                  </span>
                  <FaCaretDown className="h-3 w-3 opacity-80 transition-transform group-hover:rotate-180" />
                </button>
                <div className="absolute right-0 z-10 mt-2 w-40 overflow-hidden rounded-xl border border-gray-100 bg-white shadow-xl ring-1 ring-black/5 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all">
                  {languages
                    .filter((lang) => lang.code !== selectedLanguage)
                    .map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => handleLanguageChange(lang.code)}
                        className="flex w-full items-center gap-2 px-3 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 hover:text-gray-900"
                      >
                        <span className="inline-flex h-5 w-5 items-center justify-center">
                          {lang.icon}
                        </span>
                        <span className="flex-1 text-left">{lang.label}</span>
                      </button>
                    ))}
                </div>
              </div>
            </div>

            <nav className="space-y-1" aria-label="Mobile">
              {[...Object.keys(menuItems)].map((item) => {
                const path = null;
                const subItems = menuItems[item]?.items;

                const hasSubmenu = Boolean(menuItems[item]);
                const isExpanded = expandedItem === item;

                return (
                  <div
                    key={item}
                    className="border-b border-gray-100 pb-1 last:border-b-0"
                  >
                    <div className="flex items-center justify-between">
                      {hasSubmenu ? (
                        <button
                          type="button"
                          onClick={() => toggleSubMenu(item)}
                          className={`flex-1 text-left rounded-lg px-3 py-2 text-sm font-medium transition ${
                            isExpanded
                              ? "text-primary"
                              : "text-secondary hover:bg-gray-50"
                          }`}
                          aria-expanded={isExpanded}
                        >
                          {item}
                        </button>
                      ) : (
                        <Link
                          to={path}
                          onClick={() => setIsMenuOpen(false)}
                          className={`flex-1 rounded-lg px-3 py-2 text-sm font-medium transition ${
                            isActive(path, subItems)
                              ? "text-primary"
                              : "text-secondary hover:bg-gray-50"
                          }`}
                        >
                          {item}
                        </Link>
                      )}

                      {hasSubmenu && (
                        <button
                          type="button"
                          onClick={() => toggleSubMenu(item)}
                          className="ml-2 rounded-lg p-2 text-gray-600 hover:bg-gray-50"
                          aria-expanded={isExpanded}
                          aria-label="Toggle submenu"
                        >
                          <FaCaretDown
                            className={`h-4 w-4 transition-transform ${isExpanded ? "rotate-180" : ""}`}
                          />
                        </button>
                      )}
                    </div>

                    {hasSubmenu && isExpanded && (
                      <div className="mt-1 space-y-1 pb-2">
                        {menuItems[item].items.map((subItem) => (
                          <Link
                            key={subItem.path}
                            to={subItem.path}
                            onClick={() => setIsMenuOpen(false)}
                            className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition ${
                              location.pathname === subItem.path
                                ? "bg-gray-50 text-gray-900"
                                : "text-gray-600 hover:bg-gray-50 hover:text-gray-800"
                            }`}
                          >
                            <span className="text-base">{subItem.icon}</span>
                            <span>{subItem.label}</span>
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </nav>

            <div className="mt-4 flex items-center justify-between gap-3">
              {!user ? (
                <button
                  onClick={() => {
                    setIsMenuOpen(false);
                    handleSignIn();
                  }}
                  className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-shake"
                >
                  <span className="inline-flex items-center gap-2">
                    <FaSignInAlt className="text-xs" />
                    Sign In
                  </span>
                </button>
              ) : (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      setIsMenuOpen(false);
                      handleProfileClick();
                    }}
                    className="rounded-md bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200"
                  >
                    <span className="inline-flex items-center gap-2">
                      <FaUser className="text-xs" />
                      {user?.username || "Profile"}
                    </span>
                  </button>
                  <button
                    onClick={() => {
                      setIsMenuOpen(false);
                      handleSignOut();
                    }}
                    className="rounded-md bg-red-50 px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-100"
                  >
                    <span className="inline-flex items-center gap-2">
                      <FaSignOutAlt className="text-xs" />
                      Logout
                    </span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default PageHeader;
